/**
 * The message envelope every control-channel frame shares (doc 01 §5).
 *
 * The envelope carries what the transport must know before it looks at the
 * payload: protocol version, message id, tenant, optional account, and the
 * `account_seq` replay cursor. `payload` stays `unknown` in P1 — P2 fills in
 * the command/event schemas on top of this envelope.
 */

import { z } from 'zod';

import { REJECTION_CODES } from '@weke/domain';
import { Int64String } from './wire.js';

/** Bump on a breaking wire change. A client that cannot speak it is refused. */
export const PROTOCOL_VERSION = 1;

/** Envelope kinds. `error` carries a rejection code in its payload. */
export const EnvelopeKind = z.enum(['command', 'event', 'response', 'error']);
export type EnvelopeKind = z.infer<typeof EnvelopeKind>;

/** `cl_ord_id` — client-supplied idempotency key, ≤ 36 chars (doc 02 §1.1). */
export const ClOrdId = z.string().min(1).max(36);

/**
 * Strict on purpose: an unknown field is a client/server contract mismatch and
 * must surface at the edge, not be dropped silently.
 */
export const Envelope = z.strictObject({
  v: z.literal(PROTOCOL_VERSION),
  kind: EnvelopeKind,
  /** Message id (UUIDv7 in practice). */
  id: z.uuid(),
  /** Server time, ISO-8601 with offset. */
  ts: z.iso.datetime({ offset: true }),
  tenant_id: z.uuid(),
  account_id: z.uuid().nullable(),
  /** Replay cursor (doc 02 §1.1). Null for messages that are not account-scoped. */
  seq: Int64String.nullable(),
  /** Discriminator for the payload schema, e.g. `ORDER_PLACE`. */
  type: z.string().min(1).max(64),
  payload: z.unknown(),
});
export type Envelope = z.infer<typeof Envelope>;

/**
 * A command envelope also carries its idempotency key: `(account_id, cl_ord_id)`
 * uniquely identifies a command, and replaying it must return the original
 * result (doc 02 §6).
 */
export const CommandEnvelope = Envelope.extend({
  kind: z.literal('command'),
  account_id: z.uuid(),
  cl_ord_id: ClOrdId,
});
export type CommandEnvelope = z.infer<typeof CommandEnvelope>;

/** The closed rejection set, on the wire (doc 02 §2.9 + §2.10). */
export const RejectionCodeSchema = z.enum(REJECTION_CODES);

/** Error payload for `kind: 'error'`. */
export const RejectionPayload = z.strictObject({
  code: RejectionCodeSchema,
  message: z.string().min(1),
  /** Present when the failure was triggered by a command. */
  cl_ord_id: ClOrdId.nullable(),
});
export type RejectionPayload = z.infer<typeof RejectionPayload>;
